import { useState, useEffect, useCallback, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import PublicNav from "@/components/PublicNav";
import {
  Store, Search, Star, Plus, Pencil, Trash2, Sparkles, X, Package, ShoppingBag, Tag, Upload,
} from "lucide-react";
import AdCard from "@/components/AdCard";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const KIND_LABELS = { produit: "Produit", service: "Service" };
const STATUS_LABELS = {
  initiated: "En attente de paiement",
  paid: "Payée",
  delivered: "Livrée",
  completed: "Terminée",
  cancelled: "Annulée",
};
const STATUS_STYLES = {
  initiated: "bg-amber-50 text-amber-700",
  paid: "bg-blue-50 text-blue-700",
  delivered: "bg-brand-turquoise/10 text-brand-navy",
  completed: "bg-green-50 text-green-700",
  cancelled: "bg-slate-100 text-slate-500",
};

export const Stars = ({ value, size = "w-4 h-4" }) => (
  <span className="inline-flex items-center gap-0.5" aria-label={`${value} sur 5`}>
    {[1, 2, 3, 4, 5].map((i) => (
      <Star
        key={i}
        className={`${size} ${i <= Math.round(value) ? "fill-brand-orange text-brand-orange" : "text-slate-300"}`}
      />
    ))}
  </span>
);

export const ListingThumb = ({ images, canSee }) => {
  if (canSee && images?.length > 0) {
    return (
      <img
        src={`${API_BASE}/api/files/${images[0].file_id}/download`}
        alt=""
        className="w-full h-40 object-cover"
        loading="lazy"
      />
    );
  }
  return (
    <div className="w-full h-40 bg-brand-sky/60 flex items-center justify-center">
      <Package className="w-10 h-10 text-brand-navy/30" />
    </div>
  );
};

const ListingFormDialog = ({ initial, onClose, onSaved }) => {
  const isEdit = Boolean(initial?.listing_id);
  const [title, setTitle] = useState(initial?.title || "");
  const [description, setDescription] = useState(initial?.description || "");
  const [kind, setKind] = useState(initial?.kind || "service");
  const [price, setPrice] = useState(initial?.price || "");
  const [imageIds, setImageIds] = useState((initial?.images || []).map((i) => i.file_id));
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [suggestNote, setSuggestNote] = useState("");
  const fileRef = useRef(null);

  const suggest = async () => {
    setError("");
    setSuggestNote("");
    setSuggesting(true);
    try {
      const res = await api.post("/market/listings/suggest", { title, kind });
      if (res.data.draft?.description) setDescription(res.data.draft.description);
      if (res.data.draft?.suggested_price) setPrice(String(res.data.draft.suggested_price));
      setSuggestNote("Brouillon IA appliqué — relisez et ajustez avant de publier.");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSuggesting(false);
    }
  };

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError("");
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await api.post("/market/listings/attachments", form);
      setImageIds((ids) => [...ids, res.data.file_id]);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const save = async () => {
    setError("");
    setSaving(true);
    try {
      const payload = {
        title,
        description,
        kind,
        price: parseFloat(price),
        image_file_ids: imageIds,
      };
      const res = isEdit
        ? await api.put(`/market/listings/${initial.listing_id}`, payload)
        : await api.post("/market/listings", payload);
      onSaved(res.data);
    } catch (err) {
      setError(formatApiError(err));
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-brand-navy/40 backdrop-blur-sm flex items-center justify-center p-4">
      <div data-testid="listing-form-dialog" className="w-full max-w-lg bg-white rounded-2xl shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h2 className="font-bold text-brand-navy">{isEdit ? "Modifier l'annonce" : "Publier une annonce"}</h2>
          <button data-testid="listing-form-close" onClick={onClose} aria-label="Fermer" className="text-slate-400 hover:text-brand-navy">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          {error && (
            <div data-testid="listing-form-error" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3">{error}</div>
          )}
          {suggestNote && (
            <div data-testid="listing-form-suggest-note" className="text-sm text-brand-navy bg-brand-turquoise/10 border border-brand-turquoise/30 rounded-lg px-4 py-3">{suggestNote}</div>
          )}
          <div>
            <label className="text-sm font-medium text-brand-navy">Titre</label>
            <input
              data-testid="listing-title-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={150}
              placeholder="Ex. Cours de guitare débutant"
              className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-turquoise"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-brand-navy">Type</label>
              <select
                data-testid="listing-kind-select"
                value={kind}
                onChange={(e) => setKind(e.target.value)}
                className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-turquoise"
              >
                <option value="service">Service</option>
                <option value="produit">Produit</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-brand-navy">Prix (CAD)</label>
              <input
                data-testid="listing-price-input"
                type="number"
                min="0.01"
                step="0.01"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                placeholder="99.00"
                className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-turquoise"
              />
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-brand-navy">Description</label>
              <button
                data-testid="listing-ai-suggest-button"
                onClick={suggest}
                disabled={suggesting || !title.trim()}
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-turquoise hover:text-brand-navy disabled:opacity-40 transition-colors"
              >
                <Sparkles className="w-3.5 h-3.5" />
                {suggesting ? "L'IA rédige…" : "Suggérer avec l'IA"}
              </button>
            </div>
            <textarea
              data-testid="listing-description-input"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={5}
              maxLength={3000}
              placeholder="Décrivez votre produit ou service…"
              className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-turquoise"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-brand-navy">Images (max 5)</label>
            <div className="mt-1 flex items-center gap-2 flex-wrap">
              {imageIds.map((fid) => (
                <span key={fid} className="inline-flex items-center gap-1 text-xs bg-brand-sky text-brand-navy rounded-full px-2.5 py-1">
                  Image jointe
                  <button
                    data-testid={`listing-image-remove-${fid}`}
                    onClick={() => setImageIds((ids) => ids.filter((i) => i !== fid))}
                    aria-label="Retirer l'image"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
              {imageIds.length < 5 && (
                <button
                  data-testid="listing-image-upload-button"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-600 border border-dashed border-slate-300 rounded-lg px-3 py-2 hover:border-brand-turquoise hover:text-brand-navy disabled:opacity-50"
                >
                  <Upload className="w-3.5 h-3.5" />
                  {uploading ? "Envoi…" : "Ajouter une image"}
                </button>
              )}
              <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={uploadImage} className="hidden" data-testid="listing-image-input" />
            </div>
          </div>
          <button
            data-testid="listing-save-button"
            onClick={save}
            disabled={saving}
            className="w-full bg-brand-blue text-white font-semibold rounded-lg px-4 py-3 hover:bg-brand-navy transition-colors disabled:opacity-50"
          >
            {saving ? "Enregistrement…" : isEdit ? "Enregistrer les modifications" : "Publier l'annonce"}
          </button>
        </div>
      </div>
    </div>
  );
};

const ReviewDialog = ({ order, onClose, onReviewed }) => {
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setError("");
    setSaving(true);
    try {
      await api.post(`/market/orders/${order.order_id}/review`, { rating, comment });
      onReviewed(order.order_id);
    } catch (err) {
      setError(formatApiError(err));
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-brand-navy/40 backdrop-blur-sm flex items-center justify-center p-4">
      <div data-testid="review-dialog" className="w-full max-w-md bg-white rounded-2xl shadow-xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h2 className="font-bold text-brand-navy">Évaluer « {order.title} »</h2>
          <button data-testid="review-close" onClick={onClose} aria-label="Fermer" className="text-slate-400 hover:text-brand-navy">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          {error && (
            <div data-testid="review-error" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3">{error}</div>
          )}
          <div className="flex items-center gap-1">
            {[1, 2, 3, 4, 5].map((i) => (
              <button key={i} data-testid={`review-star-${i}`} onClick={() => setRating(i)} aria-label={`${i} étoile(s)`}>
                <Star className={`w-8 h-8 ${i <= rating ? "fill-brand-orange text-brand-orange" : "text-slate-300"}`} />
              </button>
            ))}
          </div>
          <textarea
            data-testid="review-comment-input"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={3}
            maxLength={1000}
            placeholder="Votre commentaire (optionnel)…"
            className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-turquoise"
          />
          <button
            data-testid="review-submit-button"
            onClick={submit}
            disabled={saving}
            className="w-full bg-brand-blue text-white font-semibold rounded-lg px-4 py-3 hover:bg-brand-navy transition-colors disabled:opacity-50"
          >
            {saving ? "Envoi…" : "Publier l'évaluation"}
          </button>
        </div>
      </div>
    </div>
  );
};

const OrderRow = ({ order, side, onAction }) => (
  <div data-testid={`order-${order.order_id}`} className="flex flex-wrap items-center gap-3 px-5 py-4 border-b border-slate-50">
    <div className="flex-1 min-w-[200px]">
      <Link to={`/marche/${order.listing_id}`} className="font-medium text-brand-navy text-sm hover:text-brand-blue">
        {order.title}
      </Link>
      <p className="text-xs text-slate-400 mt-0.5">
        {side === "achats" ? `Vendeur : ${order.seller_name}` : `Acheteur : ${order.buyer_name}`}
        {" · "}{order.created_at ? new Date(order.created_at).toLocaleDateString("fr-CA") : ""}
      </p>
    </div>
    <span className="text-sm font-semibold text-brand-navy">{order.amount} {order.currency}</span>
    <span data-testid={`order-status-${order.order_id}`} className={`text-xs font-medium rounded-full px-2.5 py-1 ${STATUS_STYLES[order.status] || "bg-slate-100 text-slate-500"}`}>
      {STATUS_LABELS[order.status] || order.status}
    </span>
    <div className="flex items-center gap-2">
      {side === "ventes" && order.status === "paid" && (
        <button
          data-testid={`order-deliver-${order.order_id}`}
          onClick={() => onAction("deliver", order)}
          className="text-xs font-semibold text-white bg-brand-turquoise rounded-lg px-3 py-1.5 hover:bg-brand-navy transition-colors"
        >
          Marquer livré
        </button>
      )}
      {side === "achats" && (order.status === "paid" || order.status === "delivered") && (
        <button
          data-testid={`order-complete-${order.order_id}`}
          onClick={() => onAction("complete", order)}
          className="text-xs font-semibold text-white bg-brand-turquoise rounded-lg px-3 py-1.5 hover:bg-brand-navy transition-colors"
        >
          Confirmer la réception
        </button>
      )}
      {side === "achats" && order.status === "initiated" && (
        <button
          data-testid={`order-cancel-${order.order_id}`}
          onClick={() => onAction("cancel", order)}
          className="text-xs font-semibold text-slate-600 border border-slate-300 rounded-lg px-3 py-1.5 hover:bg-slate-100 transition-colors"
        >
          Annuler
        </button>
      )}
      {side === "achats" && ["paid", "delivered", "completed"].includes(order.status) && !order.reviewed && (
        <button
          data-testid={`order-review-${order.order_id}`}
          onClick={() => onAction("review", order)}
          className="text-xs font-semibold text-brand-orange border border-brand-orange/40 rounded-lg px-3 py-1.5 hover:bg-brand-orange/10 transition-colors"
        >
          Évaluer
        </button>
      )}
      {side === "achats" && order.reviewed && (
        <span className="text-xs text-slate-400">Évaluée</span>
      )}
    </div>
  </div>
);

export default function Market() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") || "explorer";

  const [listings, setListings] = useState([]);
  const [mine, setMine] = useState([]);
  const [orders, setOrders] = useState([]);
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");
  const [kind, setKind] = useState("");
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [dialog, setDialog] = useState(null); // null | {} | listing (édition)
  const [reviewOrder, setReviewOrder] = useState(null);

  const setTab = (t) => setSearchParams(t === "explorer" ? {} : { tab: t });

  const loadExplorer = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = {};
      if (q.trim()) params.q = q.trim();
      if (kind) params.kind = kind;
      if (minPrice !== "") params.min_price = parseFloat(minPrice);
      if (maxPrice !== "") params.max_price = parseFloat(maxPrice);
      const res = await api.get("/market/listings", { params });
      setListings(res.data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [q, kind, minPrice, maxPrice]);

  const loadMine = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get("/market/listings/mine");
      setMine(res.data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadOrders = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get("/market/orders/mine");
      setOrders(res.data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadSales = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get("/market/sales");
      setSales(res.data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tab === "explorer") loadExplorer();
    else if (!user) return;
    else if (tab === "annonces") loadMine();
    else if (tab === "achats") loadOrders();
    else if (tab === "ventes") loadSales();
  }, [tab, user, loadExplorer, loadMine, loadOrders, loadSales]);

  const openCreate = () => {
    if (!user) {
      navigate("/login");
      return;
    }
    setDialog({});
  };

  const onSaved = () => {
    setDialog(null);
    if (tab === "annonces") loadMine();
    else loadExplorer();
  };

  const toggleStatus = async (listing) => {
    setError("");
    try {
      await api.put(`/market/listings/${listing.listing_id}`, {
        status: listing.status === "active" ? "suspended" : "active",
      });
      loadMine();
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const removeListing = async (listing) => {
    setError("");
    try {
      await api.delete(`/market/listings/${listing.listing_id}`);
      loadMine();
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const orderAction = async (action, order) => {
    if (action === "review") {
      setReviewOrder(order);
      return;
    }
    setError("");
    try {
      await api.post(`/market/orders/${order.order_id}/${action}`);
      if (tab === "achats") loadOrders();
      else loadSales();
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const TABS = [
    { key: "explorer", label: "Explorer" },
    ...(user
      ? [
          { key: "annonces", label: "Mes annonces" },
          { key: "achats", label: "Mes achats" },
          { key: "ventes", label: "Mes ventes" },
        ]
      : []),
  ];

  return (
    <div data-testid="market-page" className="min-h-screen bg-slate-50">
      <PublicNav />

      <div className="bg-brand-navy">
        <div className="max-w-6xl mx-auto px-6 py-12 flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-extrabold text-white flex items-center gap-3">
              <Store className="w-7 h-7 text-brand-turquoise" />
              NEXA Market
            </h1>
            <p className="text-white/70 text-sm mt-1">
              Produits et services proposés par les membres du réseau. Paiement sécurisé par PayPal.
            </p>
          </div>
          <button
            data-testid="create-listing-button"
            onClick={openCreate}
            className="inline-flex items-center gap-2 bg-brand-turquoise text-white text-sm font-semibold rounded-lg px-5 py-2.5 hover:bg-white hover:text-brand-navy transition-colors duration-200"
          >
            <Plus className="w-4 h-4" />
            Publier une annonce
          </button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">
        <AdCard placement="market" />
        <div className="mt-4 flex items-center gap-2 mb-6 border-b border-slate-200 overflow-x-auto">
          {TABS.map((t) => (
            <button
              key={t.key}
              data-testid={`market-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 -mb-px transition-colors ${
                tab === t.key
                  ? "border-brand-turquoise text-brand-navy"
                  : "border-transparent text-slate-500 hover:text-brand-navy"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {error && (
          <div data-testid="market-error" className="mb-6 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3">{error}</div>
        )}

        {tab === "explorer" && (
          <>
            <div className="flex flex-wrap items-center gap-3 mb-6">
              <div className="relative flex-1 min-w-[220px]">
                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  data-testid="market-search-input"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Rechercher un produit ou un service…"
                  className="w-full border border-slate-300 rounded-lg pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-turquoise"
                />
              </div>
              <select
                data-testid="market-kind-filter"
                value={kind}
                onChange={(e) => setKind(e.target.value)}
                className="border border-slate-300 rounded-lg px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-turquoise"
              >
                <option value="">Tous les types</option>
                <option value="produit">Produits</option>
                <option value="service">Services</option>
              </select>
              <input
                data-testid="market-min-price"
                type="number"
                min="0"
                step="0.01"
                value={minPrice}
                onChange={(e) => setMinPrice(e.target.value)}
                placeholder="Prix min"
                className="w-28 border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-turquoise"
              />
              <input
                data-testid="market-max-price"
                type="number"
                min="0"
                step="0.01"
                value={maxPrice}
                onChange={(e) => setMaxPrice(e.target.value)}
                placeholder="Prix max"
                className="w-28 border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-turquoise"
              />
            </div>

            {loading ? (
              <div className="flex justify-center py-16">
                <div className="w-10 h-10 border-4 border-slate-200 border-t-brand-turquoise rounded-full animate-spin" />
              </div>
            ) : listings.length === 0 ? (
              <div data-testid="market-empty" className="text-center py-16 text-slate-400">
                <ShoppingBag className="w-10 h-10 mx-auto mb-3 text-slate-300" />
                Aucune annonce ne correspond à votre recherche.
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {listings.map((l) => (
                  <Link
                    key={l.listing_id}
                    data-testid={`listing-card-${l.listing_id}`}
                    to={`/marche/${l.listing_id}`}
                    className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:border-brand-turquoise hover:shadow-md transition-all duration-200"
                  >
                    <ListingThumb images={l.images} canSee={Boolean(user)} />
                    <div className="p-4">
                      <div className="flex items-start justify-between gap-2">
                        <h3 className="font-semibold text-brand-navy text-sm leading-snug">{l.title}</h3>
                        <span className="text-[10px] font-semibold uppercase tracking-wide bg-brand-sky text-brand-navy rounded-full px-2 py-0.5 shrink-0">
                          {KIND_LABELS[l.kind]}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 mt-1 line-clamp-2">{l.description}</p>
                      <div className="flex items-center justify-between mt-3">
                        <span data-testid={`listing-price-${l.listing_id}`} className="font-extrabold text-brand-navy">
                          {l.price} $ {l.currency}
                        </span>
                        {l.rating_count > 0 ? (
                          <span className="inline-flex items-center gap-1 text-xs text-slate-500">
                            <Stars value={l.rating_avg} size="w-3.5 h-3.5" />
                            ({l.rating_count})
                          </span>
                        ) : (
                          <span className="text-xs text-slate-400">Nouveau</span>
                        )}
                      </div>
                      {l.seller?.nexa_id && (
                        <p className="text-xs text-slate-400 mt-2 flex items-center gap-1">
                          <Tag className="w-3 h-3" />@{l.seller.nexa_id}
                        </p>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </>
        )}

        {tab === "annonces" && user && (
          <div className="space-y-3">
            {loading ? (
              <div className="flex justify-center py-16">
                <div className="w-10 h-10 border-4 border-slate-200 border-t-brand-turquoise rounded-full animate-spin" />
              </div>
            ) : mine.length === 0 ? (
              <div data-testid="my-listings-empty" className="text-center py-16 text-slate-400">
                Vous n'avez pas encore d'annonce. Publiez votre première annonce !
              </div>
            ) : (
              mine.map((l) => (
                <div
                  key={l.listing_id}
                  data-testid={`my-listing-${l.listing_id}`}
                  className="flex flex-wrap items-center gap-3 bg-white rounded-xl border border-slate-200 px-5 py-4"
                >
                  <div className="flex-1 min-w-[200px]">
                    <Link to={`/marche/${l.listing_id}`} className="font-medium text-brand-navy text-sm hover:text-brand-blue">
                      {l.title}
                    </Link>
                    <p className="text-xs text-slate-400 mt-0.5">
                      {KIND_LABELS[l.kind]} · {l.price} $ {l.currency}
                      {l.rating_count > 0 && ` · ${l.rating_avg}/5 (${l.rating_count} avis)`}
                    </p>
                  </div>
                  <span className={`text-xs font-medium rounded-full px-2.5 py-1 ${
                    l.status === "active" ? "bg-green-50 text-green-700" : "bg-amber-50 text-amber-700"
                  }`}>
                    {l.status === "active" ? "Active" : "Suspendue"}
                  </span>
                  <button
                    data-testid={`edit-listing-${l.listing_id}`}
                    onClick={() => setDialog(l)}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-brand-blue border border-slate-300 rounded-lg px-3 py-1.5 hover:bg-slate-100"
                  >
                    <Pencil className="w-3.5 h-3.5" /> Modifier
                  </button>
                  <button
                    data-testid={`toggle-listing-${l.listing_id}`}
                    onClick={() => toggleStatus(l)}
                    className="text-xs font-semibold text-slate-600 border border-slate-300 rounded-lg px-3 py-1.5 hover:bg-slate-100"
                  >
                    {l.status === "active" ? "Suspendre" : "Réactiver"}
                  </button>
                  <button
                    data-testid={`delete-listing-${l.listing_id}`}
                    onClick={() => removeListing(l)}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-red-600 border border-red-200 rounded-lg px-3 py-1.5 hover:bg-red-50"
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Supprimer
                  </button>
                </div>
              ))
            )}
          </div>
        )}

        {tab === "achats" && user && (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            {loading ? (
              <div className="flex justify-center py-16">
                <div className="w-10 h-10 border-4 border-slate-200 border-t-brand-turquoise rounded-full animate-spin" />
              </div>
            ) : orders.length === 0 ? (
              <div data-testid="orders-empty" className="text-center py-16 text-slate-400">Aucune commande pour le moment.</div>
            ) : (
              orders.map((o) => <OrderRow key={o.order_id} order={o} side="achats" onAction={orderAction} />)
            )}
          </div>
        )}

        {tab === "ventes" && user && (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            {loading ? (
              <div className="flex justify-center py-16">
                <div className="w-10 h-10 border-4 border-slate-200 border-t-brand-turquoise rounded-full animate-spin" />
              </div>
            ) : sales.length === 0 ? (
              <div data-testid="sales-empty" className="text-center py-16 text-slate-400">Aucune vente pour le moment.</div>
            ) : (
              sales.map((o) => <OrderRow key={o.order_id} order={o} side="ventes" onAction={orderAction} />)
            )}
          </div>
        )}
      </div>

      {dialog !== null && (
        <ListingFormDialog initial={dialog.listing_id ? dialog : null} onClose={() => setDialog(null)} onSaved={onSaved} />
      )}
      {reviewOrder && (
        <ReviewDialog
          order={reviewOrder}
          onClose={() => setReviewOrder(null)}
          onReviewed={() => {
            setReviewOrder(null);
            loadOrders();
          }}
        />
      )}
    </div>
  );
}
